import React, { useState } from 'react';
import { clientService } from '../../../services/clientService';
import { useTheme } from '../../../contexts/ThemeContext';

const DeleteClientModal = ({ isOpen, client, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmationText, setConfirmationText] = useState('');

  // Check if confirmation text matches the client name
  const isConfirmationValid = () => {
    return confirmationText.trim().toLowerCase() === client?.raisonSocial?.trim().toLowerCase();
  };

  const handleDelete = async () => {
    // Validate confirmation before proceeding
    if (!isConfirmationValid()) {
      setError('Le nom de l\'entreprise ne correspond pas. Veuillez saisir exactement le nom de l\'entreprise.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await clientService.deleteClient(client.id);
      
      if (result.success) {
        onSuccess();
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('Erreur lors de la suppression du client');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError('');
    setConfirmationText('');
    onClose();
  };

  const handleConfirmationChange = (e) => {
    setConfirmationText(e.target.value);
    // Clear error when user starts typing
    if (error && error.includes('nom de l\'entreprise ne correspond pas')) {
      setError('');
    }
  };

  if (!isOpen || !client) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        {/* Header */}
        <div className={`px-6 py-4 ${
          isDarkMode 
            ? 'bg-gray-800' 
            : 'bg-gradient-to-r from-red-600 to-red-700'
        }`}>
          <div className="flex items-center">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center mr-4">
              <i className="fas fa-exclamation-triangle text-white text-xl"></i>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Confirmer la suppression</h2>
              <p className="text-red-100 text-sm">Cette action est irréversible</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Error Message */}
          {error && (
            <div className={`mb-6 border-l-4 border-l-red-500 p-4 rounded-r-lg ${
              isDarkMode 
                ? 'bg-red-900/50 text-red-300' 
                : 'bg-red-50 text-red-700'
            }`}>
              <div className="flex items-center">
                <i className={`fas fa-exclamation-circle mr-2 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`}></i>
                <span>{error}</span>
              </div>
            </div>
          )}

          {/* Client Info */}
          <div className="mb-6">
            <div className={`flex items-center p-4 rounded-xl ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center mr-4">
                {client.logo ? (
                  <img
                    src={`data:image/jpeg;base64,${client.logo}`}
                    alt={client.raisonSocial}
                    className="w-full h-full object-contain rounded-lg"
                  />
                ) : (
                  <span className="text-white font-bold text-sm">
                    {client.raisonSocial?.split(' ').slice(0, 2).map(word => word.charAt(0)).join('').toUpperCase() || 'CL'}
                  </span>
                )}
              </div>
              <div>
                <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{client.raisonSocial}</h3>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Login: {client.login}</p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>ID: {client.id}</p>
              </div>
            </div>
          </div>

          {/* Warning Message */}
          <div className={`mb-6 border-l-4 border-l-amber-500 p-4 rounded-r-lg ${
            isDarkMode 
              ? 'bg-amber-900/50' 
              : 'bg-amber-50'
          }`}>
            <div className="flex">
              <i className="fas fa-exclamation-triangle text-amber-500 mr-2 mt-0.5 flex-shrink-0"></i>
              <div>
                <h4 className={`font-medium mb-1 ${isDarkMode ? 'text-amber-300' : 'text-amber-800'}`}>Attention</h4>
                <p className={`text-sm ${isDarkMode ? 'text-amber-400' : 'text-amber-700'}`}>
                  Êtes-vous sûr de vouloir supprimer ce client ? Cette action supprimera également :
                </p>
                <ul className={`mt-2 text-sm list-disc list-inside space-y-1 ${isDarkMode ? 'text-amber-400' : 'text-amber-700'}`}>
                  <li>Toutes les données associées</li>
                  <li>L'historique des interventions</li>
                  <li>Les factures et documents</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Confirmation Input */}
          <div className="mb-6">
            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Pour confirmer, tapez exactement <span className={`font-semibold px-2 py-1 rounded ${
                isDarkMode ? 'text-red-300 bg-red-900/50' : 'text-red-600 bg-red-50'
              }`}>{client.raisonSocial}</span>
            </label>
            <input
              type="text"
              value={confirmationText}
              onChange={handleConfirmationChange}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${
                confirmationText && !isConfirmationValid()
                  ? isDarkMode 
                    ? 'border-red-600 focus:ring-red-500 bg-red-900/20 text-red-300' 
                    : 'border-red-300 focus:ring-red-500 bg-red-50 text-gray-900'
                  : confirmationText && isConfirmationValid()
                  ? isDarkMode 
                    ? 'border-green-600 focus:ring-green-500 bg-green-900/20 text-green-300' 
                    : 'border-green-300 focus:ring-green-500 bg-green-50 text-gray-900'
                  : isDarkMode 
                  ? 'border-gray-600 focus:ring-red-500 bg-gray-800 text-white placeholder-gray-400' 
                  : 'border-gray-300 focus:ring-red-500 bg-white text-gray-900 placeholder-gray-500'
              }`}
              placeholder="Tapez le nom de l'entreprise exactement..."
            />
            {/* Visual feedback for confirmation text */}
            {confirmationText && (
              <div className="mt-2 text-sm">
                {isConfirmationValid() ? (
                  <div className={`flex items-center ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                    <i className="fas fa-check-circle mr-2"></i>
                    Nom de l'entreprise confirmé
                  </div>
                ) : (
                  <div className={`flex items-center ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                    <i className="fas fa-times-circle mr-2"></i>
                    Le nom ne correspond pas
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className={`px-6 py-4 border-t ${isDarkMode ? 'bg-gray-750 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3">
            <button
              onClick={handleClose}
              disabled={loading}
              className={`px-6 py-3 border rounded-lg transition-colors font-medium disabled:opacity-50 ${
                isDarkMode 
                  ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' 
                  : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
              }`}
            >
              <i className="fas fa-times mr-2"></i>
              Annuler
            </button>
            <button
              onClick={handleDelete}
              disabled={loading || !isConfirmationValid()}
              className={`px-6 py-3 rounded-lg transition-colors font-medium flex items-center justify-center ${
                loading || !isConfirmationValid()
                  ? 'bg-gray-400 text-white cursor-not-allowed opacity-50'
                  : 'bg-red-600 text-white hover:bg-red-700'
              }`}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Suppression...
                </>
              ) : (
                <>
                  <i className="fas fa-trash mr-2"></i>
                  Supprimer définitivement
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteClientModal;